import { ConditionalRender, FilterDropdown } from '@ui';
import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, TmdxAvatar, Tooltip } from '@ds';
import { MdOutlineClear } from 'react-icons/md';
import { ORGAN_SELECT_LIST } from '@/constants/form.constants';
import { BULK_ASSIGNMENT_ROLES_LIST } from '../constants';
import { EmployeeSearchOrderBy, EmployeeStatus } from '@/api/apis/resources';
import { useInfiniteEmployeeListQuery } from '@/queries/employee';
import { useInfiniteList } from '@/hooks/useInfiniteList';
import { getFullName } from '@/utils/helpers';
import { BulkAssignmentFiltersType } from '../types';
import { NameByValue } from '@/types';
import { useBulkAssignmentSelectionValidation } from '../hooks/useBulkAssignmentSelectionValidation';
import { BulkAssignmentCase, CaseUserGroup, CaseUserGroupNames } from '@/api/apis/case';
import { roleByCaseUserGroup } from '@/constants/case/case.constants';

type BulkAssignmentFiltersProps = {
  filters: BulkAssignmentFiltersType;
  updateFilters: (value: string[], key: keyof BulkAssignmentFiltersType) => void;
  clearFilters: () => void;
  selectedCases: Record<string, BulkAssignmentCase>;
  setSelectedCases: (cases: Record<string, BulkAssignmentCase>) => void;
  isPrimary: boolean;
  setIsPrimary: (isPrimary: boolean) => void;
};

type ListBoxItem = {
  photo?: string;
} & NameByValue;

export const BulkAssignmentFilters = ({
  filters,
  updateFilters,
  clearFilters,
  selectedCases,
  setSelectedCases,
  isPrimary,
  setIsPrimary,
}: BulkAssignmentFiltersProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeNameToDetach, setEmployeeNameToDetach] = useState('');
  const [employeeNameToAssign, setEmployeeNameToAssign] = useState('');
  const { list, hasNextPage, fetchNextPage, isLoading } = useInfiniteList({
    query: useInfiniteEmployeeListQuery,
    queryParams: {
      roles: roleByCaseUserGroup[filters.group[0]],
      status: EmployeeStatus.Active,
      itemsPerPage: 20,
      orderBy: EmployeeSearchOrderBy.Name,
      asc: true,
    },
    searchTerm,
  });

  const { validateAndRemove, validateUserToAssign, validateUserToDetach } = useBulkAssignmentSelectionValidation({
    selectedCases,
    setSelectedCases,
    filters,
  });

  const usersList = useMemo(
    () => list.map((u) => ({ value: u.id, name: getFullName(u.firstName, u.lastName), photo: u.profilePhoto })) ?? [],
    [list],
  );

  const renderListBoxItem = useCallback((item: ListBoxItem) => {
    return (
      <div className="flex items-center gap-2 h-9">
        <TmdxAvatar size="md" radius="xl" src={item.photo} name={item.name} />
        <div className="text-m-m text-base-5 w-[200px] truncate text-wrap">{item.name}</div>
      </div>
    );
  }, []);

  const getName = (value?: string) => {
    if (!value) return '';
    const user = usersList.find((u) => u.value === value);
    return user ? user.name : '';
  };

  const onRoleChange = (value: string[]) => {
    updateFilters(value, 'group');
    updateFilters([], 'employeeIdsToDetach');
    updateFilters([], 'employeeIdsToAssign');
    setEmployeeNameToDetach('');
    setEmployeeNameToAssign('');
    validateAndRemove(() => true, 'the user role filter was updated');
  };

  const onUserToDetachChange = (value: string[]) => {
    updateFilters(value, 'employeeIdsToDetach');
    setEmployeeNameToDetach(getName(value[0]));
    validateUserToDetach(value[0]);
  };

  const onUserToAssignChange = (value: string[]) => {
    updateFilters(value, 'employeeIdsToAssign');
    setEmployeeNameToAssign(getName(value[0]));
    validateUserToAssign(value[0]);
  };

  const onClearFilters = () => {
    clearFilters();
    setSelectedCases({});
    setEmployeeNameToDetach('');
    setEmployeeNameToAssign('');
  };

  const { organType, group, employeeIdsToAssign, employeeIdsToDetach } = filters;
  const noRoleSelected = group.length === 0;
  const showAddAsPrimaryCheckbox = employeeIdsToAssign.length > 0 && group[0] !== CaseUserGroup.SrLogisticsManagers;

  return (
    <div className="flex items-center gap-2">
      <FilterDropdown
        title="Organ"
        items={ORGAN_SELECT_LIST}
        onSelectedKeysChange={(value) => updateFilters(value, 'organType')}
        selectedKeys={organType}
      />
      <FilterDropdown
        title={group.length > 0 ? CaseUserGroupNames[group[0]] : 'User Role'}
        mode="single"
        showCounter={false}
        listboxClassName="max-h-max"
        items={BULK_ASSIGNMENT_ROLES_LIST}
        onSelectedKeysChange={onRoleChange}
        selectedKeys={group}
      />
      <Tooltip content="Select user role first" isDisabled={!noRoleSelected}>
        <div className="flex gap-2">
          <FilterDropdown
            isDynamicItems
            mode="single"
            showCounter={false}
            triggerDisabled={noRoleSelected}
            title={employeeNameToDetach ? `Remove ${employeeNameToDetach}` : 'User to Detach'}
            items={usersList}
            onSelectedKeysChange={onUserToDetachChange}
            selectedKeys={employeeIdsToDetach}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            loadNextPage={fetchNextPage}
            onSearchValue={setSearchTerm}
            renderListBoxItem={renderListBoxItem}
          />
          <FilterDropdown
            isDynamicItems
            mode="single"
            showCounter={false}
            triggerDisabled={noRoleSelected}
            title={employeeNameToAssign ? `Add ${employeeNameToAssign}` : 'User to Add'}
            items={usersList}
            onSelectedKeysChange={onUserToAssignChange}
            selectedKeys={employeeIdsToAssign}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            loadNextPage={fetchNextPage}
            onSearchValue={setSearchTerm}
            renderListBoxItem={renderListBoxItem}
          />
        </div>
      </Tooltip>
      {showAddAsPrimaryCheckbox && (
        <Checkbox value="isPrimary" size="md" isSelected={isPrimary} onValueChange={setIsPrimary}>
          Add as Primary
        </Checkbox>
      )}
      <ConditionalRender show={!!Object.values(filters).flat().length}>
        <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="light" onPress={onClearFilters}>
          Clear all
        </Button>
      </ConditionalRender>
    </div>
  );
};
